/**
 * Mahjong tile model. A complete set is 144 tiles:
 *   - 36 dots (3 of each value 1-9 × 4 copies)
 *   - 36 bamboo
 *   - 36 characters (man/wan)
 *   - 16 winds (4 directions × 4 copies)
 *   - 12 dragons (3 colors × 4 copies)
 *   - 4 flowers (1 each, season-set)
 *   - 4 seasons (1 each)
 *
 * Match keys: numbered tiles match by exact suit+value. Winds and dragons
 * match exactly. Flowers all share the `flower` key (any flower pairs with
 * any flower); seasons share `season`. This is the most common variant of
 * mahjong solitaire matching.
 */

export type TileSuit =
  | 'dots'
  | 'bamboo'
  | 'characters'
  | 'wind'
  | 'dragon'
  | 'flower'
  | 'season';

export interface Tile {
  /** Globally unique tile id, e.g. `dots-3-a`. */
  id: string;
  suit: TileSuit;
  /** 1-9 for numbered suits, 1-4 for wind/flower/season, 1-3 for dragon. */
  value: number;
  /** Tiles share `matchKey` iff they pair under standard rules. */
  matchKey: string;
}

const COPY_LABELS = ['a', 'b', 'c', 'd'] as const;

function numberedTile(suit: TileSuit, value: number, copy: number): Tile {
  return {
    id: `${suit}-${value}-${COPY_LABELS[copy]}`,
    suit,
    value,
    matchKey: `${suit}-${value}`,
  };
}

function bonusTile(suit: 'flower' | 'season', value: number): Tile {
  return {
    id: `${suit}-${value}`,
    suit,
    value,
    matchKey: suit,
  };
}

export function createMahjongSet(): Tile[] {
  const tiles: Tile[] = [];

  for (const suit of ['dots', 'bamboo', 'characters'] as const) {
    for (let v = 1; v <= 9; v++) {
      for (let c = 0; c < 4; c++) tiles.push(numberedTile(suit, v, c));
    }
  }

  // Winds: 1=East, 2=South, 3=West, 4=North
  for (let v = 1; v <= 4; v++) {
    for (let c = 0; c < 4; c++) tiles.push(numberedTile('wind', v, c));
  }
  // Dragons: 1=Red(中), 2=Green(發), 3=White(白)
  for (let v = 1; v <= 3; v++) {
    for (let c = 0; c < 4; c++) tiles.push(numberedTile('dragon', v, c));
  }
  // Flowers/seasons: 1-4, one of each.
  for (let v = 1; v <= 4; v++) {
    tiles.push(bonusTile('flower', v));
    tiles.push(bonusTile('season', v));
  }

  return tiles;
}

export function tilesPair(a: Tile, b: Tile): boolean {
  return a.id !== b.id && a.matchKey === b.matchKey;
}
